/* Modern collection page JS
 * -------------------------
 * Loaded by sections/main-collection.liquid.
 *
 * Two responsibilities:
 *
 *   1. Mobile filter drawer — open/close lifecycle, scroll-lock.
 *   2. AJAX filter + sort + pagination updates via Shopify's Section
 *      Rendering API (?sections=main-collection). Eliminates the white
 *      flash of a full page reload when applying filters.
 *
 * Both are progressive enhancements — the page works fully without JS:
 *   - Without JS, every filter is a plain <a href> link → full page reload
 *   - Without JS, sort is a plain GET form → full page reload + manual Apply
 *   - Without JS, the drawer never opens but the desktop sidebar takes over
 *
 * State convention:
 *   - body[data-drawer-open="CollectionFilterDrawer"] → drawer open
 *   - .MainCollection__Main.is-loading              → AJAX in flight
 */
(function () {
  'use strict';

  var SECTION_ID = 'main-collection';
  var DRAWER_ID = 'CollectionFilterDrawer';

  // ===========================================================================
  // MOBILE FILTER DRAWER
  // ===========================================================================

  function openDrawer() {
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    drawer.hidden = false;
    // Force reflow before setting the attribute so the transition runs
    void drawer.offsetWidth;
    document.body.setAttribute('data-drawer-open', DRAWER_ID);
    // Move focus to the close button for keyboard users
    var close = drawer.querySelector('.CollectionFilterDrawer__Close');
    if (close) close.focus();
    updateTriggerAria(true);
  }

  function closeDrawer() {
    var drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    document.body.removeAttribute('data-drawer-open');
    updateTriggerAria(false);
    // Wait for the CSS transition before hiding entirely
    setTimeout(function () {
      if (document.body.getAttribute('data-drawer-open') !== DRAWER_ID) {
        drawer.hidden = true;
      }
    }, 260);
  }

  function updateTriggerAria(open) {
    var triggers = document.querySelectorAll('[data-drawer-open="' + DRAWER_ID + '"]');
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  // Open
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-drawer-open="' + DRAWER_ID + '"]');
    if (trigger) {
      e.preventDefault();
      openDrawer();
    }
  });

  // Close (overlay click, close button, apply button)
  document.addEventListener('click', function (e) {
    var closer = e.target.closest('[data-drawer-close]');
    if (closer && closer.closest('#' + DRAWER_ID)) {
      e.preventDefault();
      closeDrawer();
    }
  });

  // ESC to close
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && document.body.getAttribute('data-drawer-open') === DRAWER_ID) {
      closeDrawer();
    }
  });

  // ===========================================================================
  // CARD SWATCH HOVER → swap main image to that variant's image
  // Each .ProductCard__Swatch carries data-variant-image (and an optional
  // srcset). On hover we swap the card's main image; on leave we restore.
  // Pure mouse enhancement — no effect on touch (touch devices show static
  // swatches; tapping the card takes them to the product page where they
  // pick a variant properly).
  // ===========================================================================

  function swapToVariantImage(swatch) {
    var card = swatch.closest('.ProductCard');
    if (!card) return;
    var img = card.querySelector('.ProductCard__Image');
    if (!img) return;
    if (!img.dataset.originalSrc) {
      img.dataset.originalSrc = img.src;
      img.dataset.originalSrcset = img.srcset || '';
    }
    img.src = swatch.dataset.variantImage;
    img.srcset = swatch.dataset.variantImageSrcset || '';
    card.classList.add('is-swatching');
  }

  function restoreDefaultImage(swatch) {
    var card = swatch.closest('.ProductCard');
    if (!card) return;
    var img = card.querySelector('.ProductCard__Image');
    if (!img || !img.dataset.originalSrc) return;
    img.src = img.dataset.originalSrc;
    img.srcset = img.dataset.originalSrcset || '';
    card.classList.remove('is-swatching');
  }

  // Use mouseover/mouseout (bubbling) + a selector test, since mouseenter
  // doesn't bubble. We use capture for symmetry with how matches() works.
  document.addEventListener('mouseover', function (e) {
    var swatch = e.target.closest && e.target.closest('.ProductCard__Swatch[data-variant-image]');
    if (swatch) swapToVariantImage(swatch);
  });

  document.addEventListener('mouseout', function (e) {
    var swatch = e.target.closest && e.target.closest('.ProductCard__Swatch[data-variant-image]');
    if (!swatch) return;
    // Only restore if mouse left the swatch entirely (not moved to a child)
    var related = e.relatedTarget;
    if (related && swatch.contains(related)) return;
    restoreDefaultImage(swatch);
  });

  // ===========================================================================
  // AJAX FILTER + SORT + PAGINATION
  // Uses Shopify Section Rendering API: ?sections=<id> returns JSON
  // with the section's rendered HTML, which we drop in to replace the
  // current section. URL updated via history.pushState so back/forward work.
  // ===========================================================================

  // Feature detection: fetch + AbortController + URLSearchParams
  var ajaxSupported =
    typeof fetch !== 'undefined' &&
    typeof AbortController !== 'undefined' &&
    typeof URLSearchParams !== 'undefined' &&
    typeof DOMParser !== 'undefined' &&
    typeof history.pushState === 'function';

  var currentController = null;

  function getSectionEl() {
    return document.querySelector('[data-section-id="' + SECTION_ID + '"]');
  }

  function setLoading(loading) {
    var section = getSectionEl();
    if (!section) return;
    var main = section.querySelector('.MainCollection__Main');
    if (!main) return;
    main.classList.toggle('is-loading', loading);
  }

  function buildSectionUrl(rawUrl) {
    // Append sections=<id> to the URL, preserving existing query params
    var separator = rawUrl.indexOf('?') > -1 ? '&' : '?';
    return rawUrl + separator + 'sections=' + SECTION_ID;
  }

  function fetchAndReplace(url, opts) {
    opts = opts || {};

    // Abort any in-flight request
    if (currentController) currentController.abort();
    currentController = new AbortController();

    setLoading(true);

    return fetch(buildSectionUrl(url), {
      signal: currentController.signal,
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        var html = data[SECTION_ID];
        if (!html) throw new Error('Section content missing in response');

        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newSection = doc.querySelector('[data-section-id="' + SECTION_ID + '"]');
        if (!newSection) {
          // Some Shopify section responses don't wrap in the section root;
          // try the first element child of body
          newSection = doc.body.firstElementChild;
        }
        if (!newSection) throw new Error('Could not parse section HTML');

        var oldSection = getSectionEl();
        if (!oldSection) throw new Error('Old section not found in DOM');

        oldSection.parentNode.replaceChild(newSection, oldSection);

        // Update browser URL (unless we're handling a popstate)
        if (!opts.preserveUrl) {
          history.pushState({ collectionAjax: true }, '', url);
        }

        // Scroll behaviour: if we're below the section header, scroll back
        // to the top of the section so users see new results, not just an
        // unchanged-looking page mid-scroll
        var rect = newSection.getBoundingClientRect();
        if (rect.top < -40) {
          var targetY = window.pageYOffset + rect.top - 16;
          window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
        }
      })
      .catch(function (err) {
        if (err.name === 'AbortError') return; // user clicked another filter
        // Hard fallback — full page navigation
        // eslint-disable-next-line no-console
        if (window.console && console.warn) {
          console.warn('Collection AJAX fallback to full reload:', err.message);
        }
        window.location.href = url;
      })
      .finally(function () {
        setLoading(false);
        currentController = null;
      });
  }

  // Intercept clicks on filter/sort/pagination links inside the section
  if (ajaxSupported) {
    document.addEventListener('click', function (e) {
      // Don't intercept modifier-clicks (cmd-click opens new tab)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      var section = getSectionEl();
      if (!section) return;

      // Match: filter values in sidebar OR drawer, active chips, pagination
      var link = e.target.closest(
        '.CollectionFilterSidebar a[href], ' +
        '.CollectionFilterDrawer a[href], ' +
        '.CollectionPagination a[href]'
      );
      if (!link) return;

      // Only intercept links inside our section
      if (!section.contains(link)) return;

      // External URLs (just in case) — let them through
      if (link.target === '_blank') return;
      if (link.hostname && link.hostname !== window.location.hostname) return;

      e.preventDefault();

      // Mobile drawer-link case: also remove drawer scroll-lock
      if (link.closest('#' + DRAWER_ID)) {
        document.body.removeAttribute('data-drawer-open');
      }

      fetchAndReplace(link.href);
    });

    // Intercept sort dropdown change
    document.addEventListener('change', function (e) {
      if (!e.target.matches('.MainCollection__SortSelect')) return;
      var form = e.target.form;
      if (!form) return;
      e.preventDefault();
      var params = new URLSearchParams(new FormData(form)).toString();
      var url = form.action + (params ? '?' + params : '');
      fetchAndReplace(url);
    });

    // Intercept price-range form submit
    document.addEventListener('submit', function (e) {
      if (!e.target.matches('.CollectionFilterGroup__PriceForm')) return;
      e.preventDefault();
      var params = new URLSearchParams(new FormData(e.target)).toString();
      var url = e.target.action + (params ? '?' + params : '');
      fetchAndReplace(url);
    });

    // Browser back/forward
    window.addEventListener('popstate', function (e) {
      // Only handle popstates we generated
      if (e.state && e.state.collectionAjax) {
        fetchAndReplace(window.location.href, { preserveUrl: true });
      }
    });

    // Seed initial history state so back can return to this state
    if (!history.state || !history.state.collectionAjax) {
      try {
        history.replaceState({ collectionAjax: true }, '', window.location.href);
      } catch (e) { /* noop */ }
    }
  }
})();
